import { Component } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidMount() {
    // If the app runs fine for a while, re-arm the one-time auto recovery so a
    // later crash in the same tab can heal itself too (without reload loops).
    if (!this.state.error) {
      setTimeout(() => {
        try { sessionStorage.removeItem("hdh_auto_recovered"); } catch { /* private mode */ }
      }, 20000);
    }
  }

  componentDidCatch(error, info) {
    const message = String(error?.message || error).slice(0, 500);
    try {
      fetch(`${API}/client-errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          stack: String(error?.stack || "").slice(0, 4000),
          component_stack: String(info?.componentStack || "").slice(0, 4000),
          url: window.location.href,
          user_agent: navigator.userAgent,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* reporting must never break the fallback UI */
    }

    // Browser auto-translation (Safari/Chrome Translate) rewrites text nodes and
    // makes React's DOM operations fail. A single fresh reload rebuilds the DOM
    // and gets the customer back into the order flow without any action.
    const domIssue = /removeChild|insertBefore|NotFoundError|not a child of this node|Failed to execute/i.test(message);
    if (domIssue) {
      try {
        if (!sessionStorage.getItem("hdh_auto_recovered")) {
          sessionStorage.setItem("hdh_auto_recovered", "1");
          setTimeout(() => window.location.reload(), 500);
        }
      } catch {
        /* private mode — fall back to the manual reload button */
      }
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen bg-[#0A0A0B] text-white flex items-center justify-center px-6" data-testid="app-error-boundary">
        <div className="max-w-lg text-center">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Technischer Fehler</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3">
            Da ist etwas schiefgelaufen.
          </h1>
          <p className="text-zinc-400 mt-4 text-sm">
            Bitte laden Sie die Seite neu. Falls es weiterhin nicht klappt, helfen wir Ihnen
            sofort persönlich weiter — Ihre Bestellung geht nicht verloren.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="rounded-full h-12 px-7 bg-[#DC1928] hover:bg-[#B01829] text-white font-medium transition-colors"
              data-testid="error-reload-btn"
            >
              Seite neu laden
            </button>
            <a
              href="https://wa.me/436764440304"
              target="_blank"
              rel="noreferrer"
              className="rounded-full h-12 px-7 inline-flex items-center justify-center border border-white/15 text-white hover:bg-white/5 transition-colors"
              data-testid="error-whatsapp-btn"
            >
              Per WhatsApp bestellen
            </a>
          </div>
          <p className="text-[11px] text-zinc-600 mt-6">
            Oder telefonisch: <a href="tel:+436764440304" className="underline">+43 676 4440304</a>
          </p>
        </div>
      </div>
    );
  }
}
